import { useEffect, useState } from "react";
import api from "../lib/api";
import { Euro, ReceiptText, Building2, Boxes, TrendingUp } from "lucide-react";
import { useSettings } from "../context/SettingsContext";

const fmt = (n) => (n ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Bar({ label, value, max, sub, testid }) {
  const pct = max > 0 ? Math.max(4, (value / max) * 100) : 0;
  return (
    <div data-testid={testid} className="mb-3">
      <div className="flex justify-between items-baseline mb-1">
        <span className="text-sm text-slate-200 truncate pr-2">{label}</span>
        <span className="font-mono-plex text-sm text-emerald-400 shrink-0">€ {fmt(value)}</span>
      </div>
      <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
        <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function Statistiche() {
  const { text } = useSettings();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/analytics").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="text-slate-400">Caricamento statistiche…</div>;

  const maxForn = Math.max(...data.per_fornitore.map((f) => f.totale), 1);
  const maxProd = Math.max(...data.per_prodotto.map((p) => p.totale), 1);
  const maxMese = Math.max(...data.per_mese.map((m) => m.totale), 1);
  const kpis = [
    { label: "Spesa totale", value: `€ ${fmt(data.totale_speso)}`, icon: Euro, color: "text-emerald-400" },
    { label: "Bolle registrate", value: data.num_bolle, icon: ReceiptText, color: "text-purple-400" },
    { label: "Fornitori", value: data.num_fornitori, icon: Building2, color: "text-blue-400" },
    { label: "Prodotti", value: data.num_prodotti, icon: Boxes, color: "text-amber-400" },
  ];

  return (
    <div className="animate-fade-up">
      <h1 className="font-display font-black text-4xl text-slate-50 tracking-tight mb-1">{text("statistiche_title")}</h1>
      <p className="text-slate-400 mb-6">{text("statistiche_subtitle")}</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} data-testid={`kpi-${k.label}`} className="bg-slate-900/50 border border-slate-800 rounded-md p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-wider text-slate-400">{k.label}</span>
                <Icon className={`w-5 h-5 ${k.color}`} />
              </div>
              <div className="font-display font-black text-3xl text-slate-50 font-mono-plex">{k.value}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-900/50 border border-slate-800 rounded-md p-5" data-testid="stat-fornitori">
          <h2 className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-4">Spesa per fornitore</h2>
          {data.per_fornitore.length === 0 && <p className="text-slate-500 text-sm">Nessun dato.</p>}
          {data.per_fornitore.map((f) => (
            <Bar key={f.nome} label={f.nome} value={f.totale} max={maxForn} sub={`${f.num_bolle} bolle`} testid={`forn-bar-${f.nome}`} />
          ))}
        </div>

        <div className="bg-slate-900/50 border border-slate-800 rounded-md p-5" data-testid="stat-prodotti">
          <h2 className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-4">Top articoli per spesa</h2>
          {data.per_prodotto.length === 0 && <p className="text-slate-500 text-sm">Nessun dato.</p>}
          {data.per_prodotto.map((p) => (
            <Bar key={p.nome} label={p.nome} value={p.totale} max={maxProd} sub={`${fmt(p.quantita)} unità`} testid={`prod-bar-${p.nome}`} />
          ))}
        </div>

        <div className="bg-slate-900/50 border border-slate-800 rounded-md p-5" data-testid="stat-mese">
          <h2 className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-4">Andamento mensile</h2>
          {data.per_mese.length === 0 && <p className="text-slate-500 text-sm">Nessun dato.</p>}
          {data.per_mese.map((m) => (
            <Bar key={m.mese} label={m.mese} value={m.totale} max={maxMese} testid={`mese-bar-${m.mese}`} />
          ))}
        </div>

        <div className="bg-slate-900/50 border border-slate-800 rounded-md p-5" data-testid="stat-origine">
          <h2 className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-4">Spesa per origine</h2>
          <div className="flex items-center gap-2 text-slate-300 mb-4"><TrendingUp className="w-4 h-4 text-primary" /> Mercato vs Fuori mercato</div>
          <Bar label="Dal mercato" value={data.per_tipo.mercato} max={Math.max(data.per_tipo.mercato, data.per_tipo.fuori_mercato, 1)} testid="tipo-mercato" />
          <Bar label="Fuori mercato" value={data.per_tipo.fuori_mercato} max={Math.max(data.per_tipo.mercato, data.per_tipo.fuori_mercato, 1)} testid="tipo-fuori" />
        </div>
      </div>
    </div>
  );
}
